import { useEffect, useRef, useState, useCallback } from "react";

interface WebSocketRFIDData {
  rfid?: string;
  RFID?: string;
  rfidCode?: string;
  rfid_number?: string;
  data?: string | { rfid?: string; RFID?: string; rfidCode?: string };
}

interface UseWebSocketRFIDOptions {
  url: string;
  enabled?: boolean;
  onMessage?: (rfid: string) => void;
  onError?: (error: Event) => void;
  onConnect?: () => void;
  onDisconnect?: () => void;
}

interface UseWebSocketRFIDReturn {
  rfid: string | null;
  isConnected: boolean;
  error: string | null;
  connect: () => void;
  disconnect: () => void;
}

/**
 * Custom hook to connect to WebSocket and receive RFID data
 * @param options Configuration options for the WebSocket connection
 * @returns Object containing RFID data, connection status, and control functions
 */
export const useWebSocketRFID = (
  options: UseWebSocketRFIDOptions
): UseWebSocketRFIDReturn => {
  const { url, enabled = true, onMessage, onError, onConnect, onDisconnect } = options;
  const [rfid, setRfid] = useState<string | null>(null);
  const [isConnected, setIsConnected] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectAttemptsRef = useRef<number>(0);
  const statusCheckIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const isConnectingRef = useRef<boolean>(false);
  const isManualDisconnectRef = useRef<boolean>(false);
  const maxReconnectAttempts = 5;
  const reconnectDelay = 3000; // 3 seconds

  // Helper function to check actual WebSocket connection status
  const checkConnectionStatus = useCallback(() => {
    if (wsRef.current) {
      const readyState = wsRef.current.readyState;
      const actuallyConnected = readyState === WebSocket.OPEN;
      
      // Update state based on actual readyState
      setIsConnected((prevConnected) => {
        if (prevConnected !== actuallyConnected) {
          // Clear error if we're actually connected
          if (actuallyConnected) {
            setError(null);
          }
          return actuallyConnected;
        }
        return prevConnected;
      });
      
      return actuallyConnected;
    }
    return false;
  }, []);

  /**
   * Extract RFID from various possible message formats
   */
  const extractRFID = useCallback((message: string | WebSocketRFIDData): string | null => {
    try {
      // Try to parse as JSON first
      if (typeof message === "string") {
        try {
          const parsed = JSON.parse(message);
          return (
            parsed.rfid ||
            parsed.RFID ||
            parsed.rfidCode ||
            parsed.rfid_number ||
            parsed.data?.rfid ||
            parsed.data?.RFID ||
            parsed.data?.rfidCode ||
            null
          );
        } catch {
          // If not JSON, treat as plain string (RFID number)
          return message.trim() || null;
        }
      } else if (typeof message === "object") {
        // Already an object
        // Check if data is an object (not a string) before accessing its properties
        const dataObj = message.data && typeof message.data === "object" ? message.data : null;
        return (
          message.rfid ||
          message.RFID ||
          message.rfidCode ||
          message.rfid_number ||
          dataObj?.rfid ||
          dataObj?.RFID ||
          dataObj?.rfidCode ||
          null
        );
      }
    } catch (err) {
      console.error("Error extracting RFID:", err);
    }
    return null;
  }, []);

  /**
   * Connect to WebSocket
   */
  const connect = useCallback(() => {
    // Prevent multiple simultaneous connection attempts
    if (isConnectingRef.current) {
      console.log("Connection attempt already in progress, skipping...");
      return;
    }

    // If already connected, don't reconnect
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      console.log("WebSocket already connected, skipping...");
      return;
    }

    // If connecting, don't create another connection
    if (wsRef.current?.readyState === WebSocket.CONNECTING) {
      console.log("WebSocket connection in progress, skipping...");
      return;
    }

    // Clean up existing WebSocket if any
    if (wsRef.current) {
      console.log("Cleaning up existing WebSocket before creating new one");
      try {
        wsRef.current.onopen = null;
        wsRef.current.onmessage = null;
        wsRef.current.onerror = null;
        wsRef.current.onclose = null;
        if (wsRef.current.readyState !== WebSocket.CLOSED) {
          wsRef.current.close();
        }
      } catch (err) {
        console.error("Error cleaning up WebSocket:", err);
      }
      wsRef.current = null;
    }

    // Clear any pending reconnection attempts
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    isConnectingRef.current = true;
    isManualDisconnectRef.current = false;

    try {
      setError(null);
      console.log("Creating new WebSocket connection to:", url);
      const ws = new WebSocket(url);

      ws.onopen = () => {
        console.log("WebSocket connected:", url);
        isConnectingRef.current = false;
        setIsConnected(true);
        setError(null); // Clear any previous errors on successful connection
        reconnectAttemptsRef.current = 0;
        
        // Start periodic status check to ensure state stays in sync
        if (statusCheckIntervalRef.current) {
          clearInterval(statusCheckIntervalRef.current);
        }
        statusCheckIntervalRef.current = setInterval(() => {
          checkConnectionStatus();
        }, 1000); // Check every second
        
        onConnectRef.current?.();
      };

      ws.onmessage = (event) => {
        try {
          // If we're receiving messages, we're definitely connected
          setIsConnected(true);
          setError(null);
          
          console.log("WebSocket raw message received:", event.data, typeof event.data);
          const extractedRfid = extractRFID(event.data);
          console.log("Extracted RFID:", extractedRfid);
          
          if (extractedRfid) {
            setRfid(extractedRfid);
            console.log("Calling onMessage callback with RFID:", extractedRfid);
            onMessageRef.current?.(extractedRfid);
          } else {
            console.warn("No RFID extracted from message:", event.data);
          }
        } catch (err) {
          console.error("Error processing WebSocket message:", err);
        }
      };

      ws.onerror = (event) => {
        console.error("WebSocket error event:", event);
        // Only set error if the connection is actually closed
        // WebSocket can fire error events even when connected
        if (ws.readyState === WebSocket.CLOSED || ws.readyState === WebSocket.CLOSING) {
          setError("WebSocket connection error");
          setIsConnected(false);
        } else if (ws.readyState === WebSocket.OPEN) {
          // If we're still open, don't set error - connection is fine
          setError(null);
        }
        onErrorRef.current?.(event);
      };

      ws.onclose = (event) => {
        console.log("WebSocket disconnected", event.code, event.reason);
        isConnectingRef.current = false;
        setIsConnected(false);
        
        // Stop status checking
        if (statusCheckIntervalRef.current) {
          clearInterval(statusCheckIntervalRef.current);
          statusCheckIntervalRef.current = null;
        }
        
        onDisconnectRef.current?.();

        // Only attempt to reconnect if not manually disconnected and enabled
        if (!isManualDisconnectRef.current && enabled && reconnectAttemptsRef.current < maxReconnectAttempts) {
          reconnectAttemptsRef.current += 1;
          console.log(
            `Attempting to reconnect (${reconnectAttemptsRef.current}/${maxReconnectAttempts})...`
          );
          reconnectTimeoutRef.current = setTimeout(() => {
            connect();
          }, reconnectDelay);
        } else if (reconnectAttemptsRef.current >= maxReconnectAttempts) {
          setError("Failed to reconnect after multiple attempts");
        } else {
          // Clear error if we're not trying to reconnect
          setError(null);
        }
      };

      wsRef.current = ws;
    } catch (err) {
      console.error("Error creating WebSocket:", err);
      isConnectingRef.current = false;
      setError("Failed to create WebSocket connection");
      setIsConnected(false);
    }
  }, [url, enabled, extractRFID, onMessage, onError, onConnect, onDisconnect, checkConnectionStatus]);

  /**
   * Disconnect from WebSocket
   */
  const disconnect = useCallback(() => {
    console.log("Manual disconnect called");
    isManualDisconnectRef.current = true;
    isConnectingRef.current = false;
    
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (statusCheckIntervalRef.current) {
      clearInterval(statusCheckIntervalRef.current);
      statusCheckIntervalRef.current = null;
    }
    if (wsRef.current) {
      try {
        wsRef.current.onopen = null;
        wsRef.current.onmessage = null;
        wsRef.current.onerror = null;
        wsRef.current.onclose = null;
        if (wsRef.current.readyState !== WebSocket.CLOSED) {
          wsRef.current.close();
        }
      } catch (err) {
        console.error("Error closing WebSocket:", err);
      }
      wsRef.current = null;
    }
    setIsConnected(false);
    setRfid(null);
    setError(null);
  }, []);

  // Store callbacks in refs to prevent dependency issues
  const onMessageRef = useRef(onMessage);
  const onErrorRef = useRef(onError);
  const onConnectRef = useRef(onConnect);
  const onDisconnectRef = useRef(onDisconnect);
  const connectRef = useRef(connect);
  const disconnectRef = useRef(disconnect);
  
  useEffect(() => {
    onMessageRef.current = onMessage;
    onErrorRef.current = onError;
    onConnectRef.current = onConnect;
    onDisconnectRef.current = onDisconnect;
    connectRef.current = connect;
    disconnectRef.current = disconnect;
  }, [onMessage, onError, onConnect, onDisconnect, connect, disconnect]);

  // Auto-connect when enabled
  useEffect(() => {
    // Only connect if enabled and not already connected/connecting
    if (enabled) {
      if (!wsRef.current || wsRef.current.readyState === WebSocket.CLOSED) {
        connectRef.current();
      }
    } else {
      // Only disconnect if we're actually connected
      if (wsRef.current && wsRef.current.readyState !== WebSocket.CLOSED) {
        disconnectRef.current();
      }
    }

    // Cleanup on unmount only - don't disconnect on dependency changes
    return () => {
      // This cleanup only runs on unmount
      // Don't disconnect here to avoid loops - let the enabled check handle it
    };
  }, [enabled]);

  return {
    rfid,
    isConnected,
    error,
    connect,
    disconnect,
  };
};

